/* A support's readiness — the five states of the v3.3 migration ruling (pack supports-a, 12/10/2026):
   destination live · content tested · artwork reviewed · access method checked · placement confirmed.
   Never « installé »: the last state is a « mise en place ». A test is guidance, never a gate: every state is a
   to-do the merchant reads, nothing here hides a QR or refuses a publish.
   The Orchestrator's readiness model (lib-pack-16 adopted the supports-a file in place: same two exports, same five keys,
   so routes/supports never changes an import); Accueil's « À faire en priorité » reads the same states as to-dos (WF-051
   ruling: a declaration alone never ticks « content »; nothing here gates).
   What each state reads today:
   - destination: the campaign the support serves today (`servedBy(...).current`, the caller's) — live or not;
   - content: the served campaign's `testedAt` when it is one of this browser's campaigns (`getCampaign`); else false;
   - artwork, access, placement: the merchant's own declarations, kept in this browser per world (no API field yet —
     contract 06 §6.1 has none; Technical adds them, then this overlay goes). An access check is tied to the campaign
     it was made against: a re-pointed support reads « à vérifier » again (rule 3, « À vérifier après chaque changement »). */
import { currentWorld } from '@/mocks/worlds'
import { getCampaign, type CampaignLite, type SupportRef } from './campaign'

export type ReadinessKey = 'destination' | 'content' | 'artwork' | 'access' | 'placement'
export interface ReadinessState { key: ReadinessKey; label: string; done: boolean; hint: string }

interface Declared { artwork?: string; access?: { at: string; for: string | null }; placement?: { at: string } }
const key = () => `bmr.readiness.${currentWorld()}`
function readAll(): Record<string, Declared> {
  try { const raw = localStorage.getItem(key()); if (raw) { const o = JSON.parse(raw) as Record<string, Declared>; if (o && typeof o === 'object') return o } } catch { /* fine */ }
  return {}
}
function writeAll(o: Record<string, Declared>): void { try { localStorage.setItem(key(), JSON.stringify(o)) } catch { /* fine */ } }
const today = () => new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })

/** The merchant's own declaration for one state (artwork · access · placement); `done: false` withdraws it.
    `access` remembers the campaign it was checked against (`forCampaign`), so a re-pointing asks for it again. */
export function declareReadiness(supportId: string, state: 'artwork' | 'access' | 'placement', done: boolean, forCampaign: string | null = null): void {
  const all = readAll(); const d = { ...(all[supportId] ?? {}) }
  if (!done) delete d[state]
  else if (state === 'artwork') d.artwork = today()
  else if (state === 'access') d.access = { at: today(), for: forCampaign }
  else d.placement = { at: today() }
  writeAll({ ...all, [supportId]: d })
}

/** A support with a visual to review: a designed one, or a printed / on-screen genre whose standard model draws it
    (routes/supports/Imprimer `modelOf`). A member's QR and a link have none. */
const VISUAL_KINDS = ['chevalet', 'sticker', 'affiche', 'grand format', 'écran']
const hasArtwork = (s: SupportRef) => s.kind !== 'personnel' && (!!s.design || VISUAL_KINDS.includes(s.kind))

/** The five states, in the order a support is made ready. `campaign` = what it serves today (`servedBy(s, list).current`). */
export function readiness(support: SupportRef, campaign: CampaignLite | null): ReadinessState[] {
  const d = readAll()[support.id] ?? {}
  const live = !!campaign
  const tested = live ? !!getCampaign(campaign.id)?.testedAt : false
  const artwork = hasArtwork(support) ? !!d.artwork : true
  const access = !!d.access && d.access.for === (campaign?.id ?? null)
  const placement = !!d.placement
  return [
    { key: 'destination', label: live ? 'Destination en ligne' : 'Destination hors ligne', done: live,
      hint: live ? `Ouvre « ${campaign.name} ».` : 'Aucune campagne en ligne pour ce support — « Faire pointer vers… » en choisit une.' },
    { key: 'content', label: tested ? 'Parcours testé' : 'Parcours à tester', done: tested,
      hint: tested ? `Testé le ${getCampaign(campaign!.id)!.testedAt}.` : 'Jouez le parcours une fois avec « Tester ce QR » : rien ne compte dans vos résultats.' },
    { key: 'artwork', label: artwork ? 'Visuel approuvé' : 'Visuel à vérifier', done: artwork,
      hint: !hasArtwork(support) ? 'Sans design nécessaire · lien uniquement.' : artwork ? `Approuvé le ${d.artwork}.` : 'Ouvrez le PDF réel, relisez le texte, la taille et le QR, puis approuvez le visuel.' },
    { key: 'access', label: access ? 'Accès vérifié' : 'Accès à vérifier', done: access,
      hint: access ? `Vérifié le ${d.access!.at}.` : d.access ? 'La campagne a changé depuis votre vérification — à vérifier après chaque changement.' : 'Scannez le QR du support final à la distance prévue, puis déclarez votre test.' },
    { key: 'placement', label: placement ? 'Mise en place confirmée' : 'Mise en place à confirmer', done: placement,
      hint: placement ? `Confirmée le ${d.placement!.at}.` : 'Après vérification, placez le support et prévenez votre équipe.' },
  ]
}
